import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fab,
  FormControl,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import {
  Save,
  Search,
  Business,
  Warning,
  KeyboardBackspace,
  DesktopWindows,
  PhoneAndroid,
  Add,
} from '@mui/icons-material';
import { tableColumnWidths, ENVIRONMENTS_OPTIONS } from './utils/constants';
import { useAppSelector } from 'store/hook';
import { AppConfigRow } from './components/AppConfigRow';
import { ConfigRow } from './components/ConfigRow';
import { UnsavedChangesModal } from './modals/UnsavedChangesModal';
import { SaveConfirmationModal } from './modals/SaveConfirmationModal';
import NavigationMapHandler from 'handlers/navigation-map.handler';
import { CustomLoadingButton } from 'components/Buttons/CustomLoadingButton/CustomLoadingButton';
import { CustomMuiThemeProvider } from 'theme/CustomMuiThemeProvider';
import { staticNavigationMap } from 'components/Route/staticNavigationMap';
import {
  type NavItemSkeleton,
  type INavigationConfigDBItem,
} from './types/types';
import { useNavigate } from 'react-router-dom';
import { createDefaultConfig, getSkeleton } from './utils/dataFormatters';
import CustomerHandler from 'handlers/customer.handler';
import { type Customer } from 'types/customer.types';
import { SingleFadeLoader } from 'components/SingleFadeLoader';
import { useTranslation } from 'react-i18next';

export type CustomerConfigState = Record<string, INavigationConfigDBItem>;

export default function CustomerNavConfigurator() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const navigationMapHandler = new NavigationMapHandler();
  const customerHandler = new CustomerHandler();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [skeleton, setSkeleton] = useState<NavItemSkeleton[]>([]);
  const [activeConfig, setActiveConfig] = useState<CustomerConfigState>({});

  const [loading, setLoading] = useState(true);
  const [currentCustomer, setCurrentCustomer] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');

  const [isDirty, setIsDirty] = useState(false);
  const [pendingCustomer, setPendingCustomer] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState<{
    type: 'SAVE' | 'GENERATE' | null;
  }>({ type: null });

  const [activeTab, setActiveTab] = useState<'web' | 'app'>('web');
  const [appViewLoading, setAppViewLoading] = useState(false);
  const [appViewConfig, setAppViewConfig] = useState<
    Record<string, INavigationConfigDBItem>
  >({});
  const [, setAppViewAddedIds] = useState<string[]>([]);
  const [addItemModalOpen, setAddItemModalOpen] = useState(false);
  const [addItemId, setAddItemId] = useState('');
  const [addItemParentId, setAddItemParentId] = useState<string | null>(null);
  const [addItemError, setAddItemError] = useState('');
  const [copyFromCustomerCode, setCopyFromCustomerCode] = useState('');
  const [copyLoading, setCopyLoading] = useState(false);

  // const customers = useAppSelector((state) => state.customerReducer.customers);
  const customerCode = useAppSelector(
    (state) => state.authReducer.customer.code
  );

  const getNavigationMapConfig = async (
    customerCode: string,
    updateLoading: boolean = false
  ) => {
    try {
      if (updateLoading) setLoading(true);
      const response: INavigationConfigDBItem[] =
        await navigationMapHandler.getNavigationMap(false, customerCode);
      return response;
    } catch (error) {
      console.error(error);
      return [];
    } finally {
      if (updateLoading) setLoading(false);
    }
  };

  const getCustomers = async () => {
    try {
      setLoading(true);
      const response: Customer[] = await customerHandler.getAll(false);
      if (Array.isArray(response) && response?.length) {
        // update states
        const defaultCustCode = customerCode || response?.[0]?.customerCode;
        const fetchedConfig = await getNavigationMapConfig(defaultCustCode);
        const skel = getSkeleton(staticNavigationMap);
        const tempActiveConfig = createDefaultConfig(skel, fetchedConfig, t);

        const appConfigFromFetch: Record<string, INavigationConfigDBItem> = {};
        fetchedConfig
          .filter((item) => item.appEnabled)
          .forEach((item) => {
            appConfigFromFetch[item.id] = item;
          });

        setCustomers(response);
        setCurrentCustomer(defaultCustCode);
        setSkeleton(skel);
        setActiveConfig(tempActiveConfig);
        setAppViewConfig(appConfigFromFetch);
      } else {
        throw new Error("Customers isn't a valid arr");
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  // const get
  const handleConfigChange = useCallback(
    (navId: string, field: keyof INavigationConfigDBItem, value: any) => {
      setActiveConfig((previousConfig) => {
        const nextConfig = { ...previousConfig };

        const previousNodeConfig = previousConfig[navId];
        const previousPlatformEnabled = previousNodeConfig[field];

        // Update current node
        nextConfig[navId] = {
          ...previousNodeConfig,
          [field]: value,
        };

        const currentNode = skeleton.find((node) => node.id === navId);
        if (!currentNode) return nextConfig;

        const parentIndex = skeleton.indexOf(currentNode);
        const parentDepth = currentNode.depth;

        // Propagate module updates down the tree (parent + all descendants)
        if (field === 'module') {
          for (let index = parentIndex + 1; index < skeleton.length; index++) {
            const childNode = skeleton[index];
            if (childNode.depth <= parentDepth) break;

            nextConfig[childNode.id] = {
              ...nextConfig[childNode.id],
              module: value,
            };
          }

          return nextConfig;
        }

        // Only hierarchy logic for platform flags
        if (field !== 'webEnabled' && field !== 'appEnabled') {
          return nextConfig;
        }

        /**
         * Parent ON   enable all children for this platform
         * Parent OFF  disable all children for this platform
         */
        for (let index = parentIndex + 1; index < skeleton.length; index++) {
          const childNode = skeleton[index];
          if (childNode.depth <= parentDepth) break;

          nextConfig[childNode.id] = {
            ...nextConfig[childNode.id],
            [field]: value,
          };
        }

        /**
         * Child OFF  ON transition enables parents for this platform
         */
        if (!previousPlatformEnabled && value === true) {
          let tempNode = currentNode;

          while (tempNode.parentId) {
            const parentNode = skeleton.find(
              (node) => node.id === tempNode.parentId
            );
            if (!parentNode) break;

            nextConfig[parentNode.id] = {
              ...nextConfig[parentNode.id],
              [field]: true,
            };

            tempNode = parentNode;
          }
        }

        /**
         * Disable parent platform ONLY if all siblings are disabled for that platform
         */
        if (previousPlatformEnabled && value === false) {
          let tempNode = currentNode;

          while (tempNode.parentId) {
            const parentId = tempNode.parentId;

            const siblings = skeleton.filter(
              (node) => node.parentId === parentId
            );

            const hasAnyPlatformEnabledSibling = siblings.some((sibling) => {
              return nextConfig[sibling.id]?.[field];
            });

            if (!hasAnyPlatformEnabledSibling) {
              nextConfig[parentId] = {
                ...nextConfig[parentId],
                [field]: false,
              };

              const parentNode = skeleton.find((node) => node.id === parentId);
              if (!parentNode) break;

              tempNode = parentNode;
            } else {
              break;
            }
          }
        }

        return nextConfig;
      });

      setIsDirty(true);
    },
    [skeleton]
  );

  const performCustomerSwitch = async (newCode: string) => {
    try {
      const fetchedConfig = await getNavigationMapConfig(newCode, true);
      setCurrentCustomer(newCode);
      setPendingCustomer(null);
      setIsDirty(false);
      const tempActiveConfig = createDefaultConfig(skeleton, fetchedConfig, t);
      const appConfigFromFetch: Record<string, INavigationConfigDBItem> = {};
      fetchedConfig
        .filter((item) => item.appEnabled)
        .forEach((item) => {
          appConfigFromFetch[item.id] = item;
        });
      setActiveConfig(tempActiveConfig);
      setAppViewConfig(appConfigFromFetch);
    } catch (error) {
      console.error(error);
    }
  };

  const initiateCustomerSwitch = async (newCode: string) => {
    if (isDirty) {
      setPendingCustomer(newCode);
      setModalOpen({ type: 'SAVE' });
    } else {
      await performCustomerSwitch(newCode);
    }
  };

  const handleDiscardAndSwitch = async () => {
    if (pendingCustomer) {
      await performCustomerSwitch(pendingCustomer);
    }
    setModalOpen({ type: null });
  };

  const handleSaveClick = () => {
    setModalOpen({ type: 'GENERATE' });
  };

  const handleConfirmGenerate = async () => {
    try {
      const allIds = [
        ...new Set([
          ...skeleton.map((n) => n.id),
          ...Object.keys(appViewConfig),
        ]),
      ];
      const mergedConfig = { ...activeConfig, ...appViewConfig };
      const rawRows = allIds
        .map((navId) => {
          const item = mergedConfig[navId];
          if (!item?.id) return null;
          return {
            customerCode: currentCustomer,
            id: item.id,
            label: item.label,
            roles: item.roles ?? [],
            environments: item.environments ?? [],
            invisible: item.invisible ?? false,
            path: item.path ?? '',
            webEnabled: item.webEnabled ?? false,
            appEnabled: item.appEnabled ?? false,
            order: item.order ?? null,
            parentId: item.parentId ?? null,
            showPro: item.showPro ?? false,
            module: (item.module ?? '').trim(),
          };
        })
        .filter(Boolean) as INavigationConfigDBItem[];
      const byParent = new Map<string | null, INavigationConfigDBItem[]>();
      rawRows.forEach((row) => {
        const pid = row.parentId ?? null;
        const arr = byParent.get(pid) ?? [];
        arr.push(row);
        byParent.set(pid, arr);
      });
      const normalizedOrder = new Map<string, number>();
      byParent.forEach((rows, parentId) => {
        rows.sort(
          (a, b) =>
            (a.order ?? 1e9) - (b.order ?? 1e9) ||
            (a.id || '').localeCompare(b.id || '')
        );
        const start = parentId === null ? 0 : 1;
        rows.forEach((row, i) => {
          normalizedOrder.set(row.id, start + i);
        });
      });
      const finalRows = rawRows.map((row) => ({
        ...row,
        order: normalizedOrder.get(row.id) ?? row.order ?? 0,
      })) as INavigationConfigDBItem[];
      await navigationMapHandler.createNavigationMap(
        currentCustomer,
        finalRows
      );
      setIsDirty(false);
      setModalOpen({ type: null });
      if (activeTab === 'app') {
        const configMap: Record<string, INavigationConfigDBItem> = {};
        finalRows
          .filter((item) => item.appEnabled)
          .forEach((item) => {
            configMap[item.id] = item;
          });
        setAppViewConfig(configMap);
        setAppViewAddedIds([]);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const filteredSkeleton = useMemo(() => {
    if (searchTerm) {
      return skeleton.filter(
        (i) =>
          i.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
          i.defaultLabel.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    return skeleton;
  }, [skeleton, searchTerm]);

  const appViewRows = useMemo(() => {
    const entries = Object.entries(appViewConfig).map(([configKey, item]) => ({
      configKey,
      id: item.id,
      path: item.path ?? '',
      order: item.order ?? null,
      parentId: item.parentId ?? null,
      defaultLabel: item.label ?? '',
      showPro: item.showPro ?? false,
    }));
    const byId = new Map(entries.map((e) => [e.id, e]));
    const getDepth = (parentId: string | null): number =>
      parentId ? 1 + getDepth(byId.get(parentId)?.parentId ?? null) : 0;
    const withDepth = entries.map((e) => ({
      ...e,
      depth: getDepth(e.parentId),
      hasChildren: entries.some((x) => x.parentId === e.id),
    }));
    const byParent = new Map<string | null, typeof withDepth>();
    withDepth.forEach((e) => {
      const pid = e.parentId;
      let arr = byParent.get(pid);
      if (!arr) {
        arr = [];
        byParent.set(pid, arr);
      }
      arr.push(e);
    });
    byParent.forEach((arr) =>
      arr.sort((a, b) => {
        const oA = a.order ?? 1e9;
        const oB = b.order ?? 1e9;
        if (oA !== oB) return oA - oB;
        return (a.id || '').localeCompare(b.id || '');
      })
    );
    const flattened: typeof withDepth = [];
    const appendChildren = (parentId: string | null) => {
      const children = byParent.get(parentId) ?? [];
      children.forEach((node) => {
        flattened.push(node);
        appendChildren(node.id);
      });
    };
    appendChildren(null);
    return flattened;
  }, [appViewConfig]);

  const getAppViewItem = useCallback(
    (navId: string): INavigationConfigDBItem => {
      return (
        appViewConfig[navId] ||
        activeConfig[navId] || {
          id: navId,
          webEnabled: false,
          appEnabled: true,
          label: '',
          roles: [],
          environments: [],
          invisible: false,
          path: '',
          customerCode: currentCustomer,
        }
      );
    },
    [appViewConfig, activeConfig, currentCustomer]
  );

  const getItem = (navId: string): INavigationConfigDBItem => {
    return (
      activeConfig[navId] || {
        id: navId,
        webEnabled: false,
        appEnabled: false,
        label: '',
        roles: [],
        environments: [],
        invisible: false,
        path: '',
        customerCode: currentCustomer,
        showPro: false,
      }
    );
  };

  const CUSTOMER_CODE_OPTIONS = useMemo(() => {
    if (Array.isArray(customers) && customers.length) {
      const temp = customers.map((el) => ({
        name: el.customerName,
        code: el.customerCode,
      }));

      const sorted = [...temp]?.sort((a, b) => a?.name?.localeCompare(b?.name));
      return sorted;
    }
    return [];
  }, [customers]);

  const otherCustomersForCopy = useMemo(
    () => CUSTOMER_CODE_OPTIONS.filter((c) => c.code !== currentCustomer),
    [CUSTOMER_CODE_OPTIONS, currentCustomer]
  );

  useEffect(() => {
    void getCustomers();
  }, [customerCode]);

  const fetchAppViewConfig = useCallback(async () => {
    if (!currentCustomer) return;
    setAppViewLoading(true);
    try {
      const response = await getNavigationMapConfig(currentCustomer, false);
      const configMap: Record<string, INavigationConfigDBItem> = {};
      response
        .filter((item) => item.appEnabled)
        .forEach((item) => {
          configMap[item.id] = item;
        });
      setAppViewConfig(configMap);
    } catch (e) {
      setAppViewConfig({});
    } finally {
      setAppViewLoading(false);
    }
  }, [currentCustomer]);

  useEffect(() => {
    if (activeTab === 'app' && currentCustomer) {
      void fetchAppViewConfig();
    }
  }, [activeTab, currentCustomer, fetchAppViewConfig]);

  useEffect(() => {
    setAppViewAddedIds([]);
  }, [currentCustomer]);

  const handleAppViewConfigChange = useCallback(
    (navId: string, field: keyof INavigationConfigDBItem, value: any) => {
      setAppViewConfig((prev) => {
        let next: Record<string, INavigationConfigDBItem> = {
          ...prev,
          [navId]: {
            ...(prev[navId] || activeConfig[navId]),
            [field]: value,
          },
        };
        if (field === 'order' && typeof value === 'number') {
          const updatedItem = next[navId];
          const parentId = updatedItem?.parentId ?? null;
          const idsWithSameOrder = Object.entries(next)
            .filter(
              ([key, item]) =>
                key !== navId &&
                (item.parentId ?? null) === parentId &&
                (item.order ?? null) === value
            )
            .map(([k]) => k);
          idsWithSameOrder.forEach((key) => {
            const siblingOrders = Object.values(next)
              .filter((i) => (i.parentId ?? null) === parentId)
              .map((i) => i.order)
              .filter(
                (v): v is number =>
                  typeof v === 'number' && Number.isFinite(v) && v >= 0
              );
            const nextOrder =
              siblingOrders.length > 0
                ? Math.max(...siblingOrders, 0) + 1
                : parentId === null
                ? 0
                : 1;
            next = {
              ...next,
              [key]: { ...next[key], order: nextOrder },
            };
          });
        }
        return next;
      });
      setIsDirty(true);
    },
    [activeConfig]
  );

  const handleMoveAppRowUp = useCallback(
    (configKey: string) => {
      const idx = appViewRows.findIndex((r) => r.configKey === configKey);
      if (idx <= 0) return;
      const current = appViewRows[idx];
      const prev = appViewRows[idx - 1];
      if (prev.parentId !== current.parentId) return;
      const currentItem = getAppViewItem(configKey);
      const prevItem = getAppViewItem(prev.configKey);
      const newCurrentOrder = prevItem.order ?? 0;
      const newPrevOrder = currentItem.order ?? 0;
      setAppViewConfig((prevConfig) => ({
        ...prevConfig,
        [configKey]: {
          ...currentItem,
          parentId: current.parentId,
          order: newCurrentOrder,
        },
        [prev.configKey]: {
          ...prevItem,
          parentId: prev.parentId,
          order: newPrevOrder,
        },
      }));
      setIsDirty(true);
    },
    [appViewRows, getAppViewItem]
  );

  const handleMoveAppRowDown = useCallback(
    (configKey: string) => {
      const idx = appViewRows.findIndex((r) => r.configKey === configKey);
      if (idx < 0 || idx >= appViewRows.length - 1) return;
      const current = appViewRows[idx];
      const next = appViewRows[idx + 1];
      if (next.parentId !== current.parentId) return;
      const currentItem = getAppViewItem(configKey);
      const nextItem = getAppViewItem(next.configKey);
      const newCurrentOrder = nextItem.order ?? 0;
      const newNextOrder = currentItem.order ?? 0;
      setAppViewConfig((prevConfig) => ({
        ...prevConfig,
        [configKey]: {
          ...currentItem,
          parentId: current.parentId,
          order: newCurrentOrder,
        },
        [next.configKey]: {
          ...nextItem,
          parentId: next.parentId,
          order: newNextOrder,
        },
      }));
      setIsDirty(true);
    },
    [appViewRows, getAppViewItem]
  );

  const handleCopyFromCustomer = useCallback(async () => {
    if (!copyFromCustomerCode || !currentCustomer) return;
    setCopyLoading(true);
    try {
      const response = await getNavigationMapConfig(
        copyFromCustomerCode,
        false
      );
      const appOnly = response.filter(
        (item: INavigationConfigDBItem) => item.appEnabled
      );
      const copiedMap: Record<string, INavigationConfigDBItem> = {};
      appOnly.forEach((item: INavigationConfigDBItem) => {
        copiedMap[item.id] = {
          ...item,
          customerCode: currentCustomer,
          navId: undefined,
        };
      });
      setAppViewConfig(copiedMap);
      setAppViewAddedIds([]);
      setIsDirty(true);
      setCopyFromCustomerCode('');
    } catch (e) {
      console.error(e);
    } finally {
      setCopyLoading(false);
    }
  }, [copyFromCustomerCode, currentCustomer]);

  const appViewParentOptions = useMemo(() => {
    return appViewRows
      .map((r) => ({
        id: r.id,
        label: getAppViewItem(r.configKey).label || r.id,
      }))
      .filter((o) => o.id);
  }, [appViewRows, getAppViewItem]);

  const getNextAppOrder = useCallback(
    (parentId: string | null): number => {
      const siblings = Object.values(appViewConfig).filter(
        (i) => (i.parentId ?? null) === parentId
      );
      const orders = siblings
        .map((i) => i.order)
        .filter(
          (v): v is number =>
            typeof v === 'number' && Number.isFinite(v) && v >= 0
        );
      if (parentId === null) {
        return orders.length ? Math.max(...orders, -1) + 1 : 0;
      }
      return orders.length ? Math.max(...orders, 0) + 1 : 1;
    },
    [appViewConfig]
  );

  const handleOpenAddItemModal = useCallback(() => {
    setAddItemId('');
    setAddItemParentId(null);
    setAddItemError('');
    setAddItemModalOpen(true);
  }, []);

  const handleCloseAddItemModal = useCallback(() => {
    setAddItemModalOpen(false);
    setAddItemId('');
    setAddItemParentId(null);
    setAddItemError('');
  }, []);

  const handleConfirmAddAppItem = useCallback(() => {
    const id = addItemId.trim();
    if (!id) {
      setAddItemError('ID is required.');
      return;
    }
    const idExists =
      skeleton.some((n) => n.id === id) ||
      Object.values(appViewConfig).some((item) => item.id === id);
    if (idExists) {
      setAddItemError('This ID already exists.');
      return;
    }
    setAddItemError('');
    const newKey = `newItem-${Date.now()}`;
    const newItem: INavigationConfigDBItem = {
      id,
      webEnabled: false,
      appEnabled: true,
      label: '',
      roles: [],
      environments: [...ENVIRONMENTS_OPTIONS],
      invisible: true,
      path: '',
      customerCode: currentCustomer,
      order: getNextAppOrder(addItemParentId),
      parentId: addItemParentId ?? null,
      showPro: false,
    };
    setAppViewConfig((prev) => ({ ...prev, [newKey]: newItem }));
    setAppViewAddedIds((prev) => [...prev, newKey]);
    setIsDirty(true);
    handleCloseAddItemModal();
  }, [
    addItemId,
    addItemParentId,
    currentCustomer,
    getNextAppOrder,
    skeleton,
    appViewConfig,
    handleCloseAddItemModal,
  ]);

  return (
    <CustomMuiThemeProvider>
      <Box
        sx={{
          minHeight: '100vh',
          p: 3,
        }}
      >
        <Container maxWidth="xl" sx={{ p: '0 !important' }}>
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              mb: 3,
              borderRadius: 3,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 3,
              position: 'sticky',
              top: 0,
              zIndex: 100,
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CustomLoadingButton
                startIcon={<KeyboardBackspace />}
                onClick={handleBack}
              >
                {t('cdp_button_back')}
              </CustomLoadingButton>
              <Box
                sx={{
                  px: 2,
                  py: 1,
                  borderRadius: 2,
                  bgcolor: '#4F46E5',
                  color: '#FFFFFF',
                }}
              >
                <Business />
              </Box>
              <Box display={'flex'} alignItems={'center'} gap={2}>
                <FormControl variant="standard" sx={{ display: 'block' }}>
                  <Select
                    value={currentCustomer}
                    onChange={(e) => {
                      const value = e.target.value;
                      void initiateCustomerSwitch(value);
                    }}
                    disableUnderline
                    disabled={loading}
                    sx={{
                      fontSize: '1.25rem',
                      fontWeight: 500,
                      '& .MuiSelect-select': { py: 0, pr: 3 },
                    }}
                  >
                    {CUSTOMER_CODE_OPTIONS.map((c) => (
                      <MenuItem key={c.code} value={c.code}>
                        {c.name} ({c.code.toUpperCase()})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {isDirty && (
                  <Chip
                    label={t('unsaved_changes')}
                    size="small"
                    color="warning"
                    icon={<Warning />}
                    sx={{ p: 1 }}
                  />
                )}
              </Box>
            </Box>

            <Stack direction="row" spacing={2} alignItems="center">
              <TextField
                size="small"
                placeholder={t('search_screens')}
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                }}
                disabled={loading}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  width: 250,
                  '& fieldset': { borderRadius: 2 },
                }}
              />

              {activeTab === 'app' && (
                <CustomLoadingButton
                  startIcon={<Add />}
                  onClick={handleOpenAddItemModal}
                  variant="contained"
                  color="primary"
                >
                  {t('add_new_item')}
                </CustomLoadingButton>
              )}

              <CustomLoadingButton
                disabled={loading}
                loading={loading}
                sx={{
                  width: '100px',
                }}
                startIcon={<Save />}
                onClick={handleSaveClick}
                color="success"
              >
                {t('save')}
              </CustomLoadingButton>
            </Stack>
          </Paper>

          <Tabs
            value={activeTab}
            onChange={(_, v: 'web' | 'app') => {
              setActiveTab(v);
            }}
            sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab
              value="web"
              label={t('web_portal_navigation')}
              icon={<DesktopWindows />}
              iconPosition="start"
            />
            <Tab
              value="app"
              label={t('mobile_app_navigation')}
              icon={<PhoneAndroid />}
              iconPosition="start"
            />
          </Tabs>

          {loading ? (
            <Paper
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                height: '100vh',
              }}
            >
              <SingleFadeLoader isFullScreen={false} />
            </Paper>
          ) : activeTab === 'web' ? (
            <Paper sx={{ borderRadius: 2 }}>
              <Table stickyHeader size="small" sx={{ borderRadius: 2 }}>
                <TableHead sx={{ borderRadius: 2 }}>
                  <TableRow>
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        py: 2,
                        pl: 3,
                        width: tableColumnWidths.screen,
                        borderRadius: '8px 0px 0px 0px',
                        backgroundColor: 'background.paper',
                      }}
                    >
                      Screen
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        py: 2,
                        width: tableColumnWidths.webEnabled,
                        textAlign: 'center',
                        backgroundColor: 'background.paper',
                      }}
                    >
                      Web
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        py: 2,
                        width: tableColumnWidths.module,
                        textAlign: 'center',
                        backgroundColor: 'background.paper',
                      }}
                    >
                      Module
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        py: 2,
                        width: tableColumnWidths.displayLabel,
                        backgroundColor: 'background.paper',
                      }}
                    >
                      {t('label')}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        py: 2,
                        width: tableColumnWidths.allowedRoles,
                        backgroundColor: 'background.paper',
                      }}
                    >
                      {t('roles')}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        py: 2,
                        width: tableColumnWidths.environments,
                        backgroundColor: 'background.paper',
                      }}
                    >
                      {t('environments')}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        py: 2,
                        textAlign: 'center',
                        width: '8%',
                        backgroundColor: 'background.paper',
                      }}
                    >
                      {t('show_pro')}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        py: 2,
                        textAlign: 'center',
                        width: tableColumnWidths.showInSidebar,
                        borderRadius: '0px 8px 0px 0px',
                        backgroundColor: 'background.paper',
                      }}
                    >
                      {t('side_bar_visibilty')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredSkeleton.map((node) => (
                    <ConfigRow
                      key={node.id}
                      node={node}
                      config={getItem(node.id)}
                      onUpdate={handleConfigChange}
                    />
                  ))}
                </TableBody>
              </Table>
            </Paper>
          ) : appViewLoading ? (
            <Paper
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                minHeight: 300,
              }}
            >
              <SingleFadeLoader isFullScreen={false} />
            </Paper>
          ) : appViewRows.length === 0 ? (
            <Paper sx={{ borderRadius: 2, p: 3 }}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body1" color="text.secondary">
                  {t('no_app_enabled_routes_copy_from_another_customer')}
                </Typography>
              </Box>
              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                flexWrap="wrap"
              >
                <FormControl size="small" sx={{ minWidth: 220 }}>
                  <Select
                    displayEmpty
                    value={copyFromCustomerCode}
                    onChange={(e) => {
                      setCopyFromCustomerCode(e.target.value);
                    }}
                    renderValue={(selected) => {
                      if (!selected) return 'Select customer';
                      const opt = otherCustomersForCopy.find(
                        (c) => c.code === selected
                      );
                      return opt ? `${opt.name} (${opt.code})` : selected;
                    }}
                  >
                    <MenuItem value="">
                      <em>{t('select_customer')}</em>
                    </MenuItem>
                    {otherCustomersForCopy.map((c) => (
                      <MenuItem key={c.code} value={c.code}>
                        {c.name} ({c.code})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <CustomLoadingButton
                  variant="contained"
                  disabled={!copyFromCustomerCode || copyLoading}
                  loading={copyLoading}
                  onClick={() => {
                    void handleCopyFromCustomer();
                  }}
                >
                  Copy
                </CustomLoadingButton>
              </Stack>
            </Paper>
          ) : (
            <Paper sx={{ borderRadius: 2 }}>
              <Table stickyHeader size="small" sx={{ borderRadius: 2 }}>
                <TableHead sx={{ borderRadius: 2 }}>
                  <TableRow>
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        py: 2,
                        pl: 3,
                        width: '8%',
                        backgroundColor: 'background.paper',
                      }}
                    >
                      {t('position')}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        py: 2,
                        width: '14%',
                        borderRadius: '8px 0px 0px 0px',
                        backgroundColor: 'background.paper',
                      }}
                    >
                      {t('structure_route')}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        py: 2,
                        width: '12%',
                        backgroundColor: 'background.paper',
                      }}
                    >
                      {t('path')}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        py: 2,
                        width: '10%',
                        backgroundColor: 'background.paper',
                      }}
                    >
                      Parent
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        py: 2,
                        width: tableColumnWidths.displayLabel,
                        backgroundColor: 'background.paper',
                      }}
                    >
                      {t('label')}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        py: 2,
                        width: tableColumnWidths.allowedRoles,
                        backgroundColor: 'background.paper',
                      }}
                    >
                      Roles
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        py: 2,
                        width: tableColumnWidths.environments,
                        backgroundColor: 'background.paper',
                      }}
                    >
                      Environments
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        py: 2,
                        textAlign: 'center',
                        width: '8%',
                        backgroundColor: 'background.paper',
                      }}
                    >
                      Visible
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        py: 2,
                        textAlign: 'center',
                        width: tableColumnWidths.showInSidebar,
                        borderRadius: '0px 8px 0px 0px',
                        backgroundColor: 'background.paper',
                      }}
                    >
                      {t('show_pro')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {appViewRows.map((node, rowIndex) => {
                    const siblings = appViewRows.filter(
                      (r) => r.parentId === node.parentId
                    );
                    const position =
                      siblings.findIndex(
                        (s) => s.configKey === node.configKey
                      ) + 1;
                    const prevRow =
                      rowIndex > 0 ? appViewRows[rowIndex - 1] : null;
                    const nextRow =
                      rowIndex < appViewRows.length - 1
                        ? appViewRows[rowIndex + 1]
                        : null;
                    const canMoveUp =
                      prevRow !== null && prevRow.parentId === node.parentId;
                    const canMoveDown =
                      nextRow !== null && nextRow.parentId === node.parentId;
                    return (
                      <AppConfigRow
                        key={node.configKey}
                        configKey={node.configKey}
                        config={getAppViewItem(node.configKey)}
                        onUpdate={handleAppViewConfigChange}
                        depth={node.depth}
                        parentOptions={appViewParentOptions.filter(
                          (o) => o.id !== node.id
                        )}
                        positionLabel={`${position} of ${siblings.length}`}
                        canMoveUp={canMoveUp}
                        canMoveDown={canMoveDown}
                        onMoveUp={() => {
                          handleMoveAppRowUp(node.configKey);
                        }}
                        onMoveDown={() => {
                          handleMoveAppRowDown(node.configKey);
                        }}
                      />
                    );
                  })}
                </TableBody>
              </Table>
            </Paper>
          )}
        </Container>

        {modalOpen.type === 'SAVE' && (
          <UnsavedChangesModal
            currentCustomer={currentCustomer}
            handleDiscardAndSwitch={handleDiscardAndSwitch}
            modalOpen={modalOpen}
            setModalOpen={setModalOpen}
          />
        )}

        {modalOpen.type === 'GENERATE' && (
          <SaveConfirmationModal
            currentCustomer={currentCustomer}
            handleGenerate={handleConfirmGenerate}
            modalOpen={modalOpen}
            setModalOpen={setModalOpen}
          />
        )}

        <Dialog
          open={addItemModalOpen}
          onClose={handleCloseAddItemModal}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>{t('add_new_item_app_navigation')}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
              {addItemError && (
                <Box sx={{ color: 'error.main', fontSize: '0.875rem' }}>
                  {addItemError}
                </Box>
              )}
              <TextField
                label="ID"
                value={addItemId}
                onChange={(e) => {
                  setAddItemId(e.target.value);
                  setAddItemError('');
                }}
                placeholder={t('e_g_fleet_insight')}
                fullWidth
                required
                size="small"
              />
              <FormControl fullWidth size="small">
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mb: 0.5, display: 'block' }}
                >
                  {t('parent_optional_leave_empty_for_top_level')}
                </Typography>
                <Select
                  displayEmpty
                  value={addItemParentId ?? ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    setAddItemParentId(v === '' ? null : v);
                  }}
                  renderValue={(selected) => {
                    if (!selected) return <em>{t('none_top_level')}</em>;
                    const opt = appViewParentOptions.find(
                      (o) => o.id === selected
                    );
                    return opt ? `${opt.label} (${opt.id})` : selected;
                  }}
                >
                  <MenuItem value="">
                    <em>{t('none_top_level')}</em>
                  </MenuItem>
                  {appViewParentOptions.map((o) => (
                    <MenuItem key={o.id} value={o.id}>
                      {o.label} ({o.id})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handleCloseAddItemModal}>{t('cancel')}</Button>
            <Button
              variant="contained"
              onClick={handleConfirmAddAppItem}
              disabled={!addItemId.trim()}
            >
              Add
            </Button>
          </DialogActions>
        </Dialog>

        {activeTab === 'app' && (
          <Fab
            color="primary"
            aria-label={t('add_new_item')}
            onClick={handleOpenAddItemModal}
            sx={{
              position: 'fixed',
              bottom: 24,
              right: 24,
            }}
          >
            <Add />
          </Fab>
        )}
      </Box>
    </CustomMuiThemeProvider>
  );
}
